import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments';
import { GrantTypeEnum } from '../../account.data';
import { AccountBuilder } from './account.builder';
import {
  Account,
  Challenge,
  LoginAttemptResult,
  LoginResult,
  Referer,
  SetToken,
} from '../models';
import {
  IAccountLoginRequest,
  IAccountLoginRequestPayload,
  IAccountRegistrationRequest,
  IChangePasswordTokenRequestPayload,
  ICompanyLoginRequestPayload,
  ISetPasswordRequestPayload,
  ISetPasswordTokenRequestPayload,
  IUpdatePasswordRequestPayload,
} from './account-service.data';
import { HttpService, IResponseInfo } from 'i2g-ng-utils';

@Injectable()
export class AccountService {
  constructor(
    private _httpService: HttpService,
    private _accountBuilder: AccountBuilder,
    private _translateService: TranslateService
  ) {}

  getReferringCompany(referralCode: string): Observable<Referer> {
    const url = `${environment.gatewayUrl}/refer_a_friend/company_info`;
    const queryParams = this._accountBuilder.buildReferringCompanyQueryParameters(
      referralCode
    );

    return this._httpService.get(url, {}, queryParams).pipe(
      map(({ body }: IResponseInfo) => {
        return new Referer({
          ...body,
          referral_code: referralCode,
        });
      })
    );
  }

  registerAccountByEmail(
    email: string,
    password: string,
    locale: string,
    referralCode?: string
  ): Observable<Account> {
    const request = this._accountBuilder.buildAccountRegistrationRequest(
      GrantTypeEnum.password,
      email,
      password,
      locale,
      referralCode
    );

    return this.registerAccount(request);
  }

  registerAccountByGoogle(
    email: string,
    idToken: string,
    locale: string,
    referralCode?: string
  ): Observable<Account> {
    const request = this._accountBuilder.buildAccountRegistrationRequest(
      GrantTypeEnum.google,
      email,
      idToken,
      locale,
      referralCode
    );

    return this.registerAccount(request);
  }

  registerAccountByFacebook(
    email: string,
    idToken: string,
    locale: string,
    referralCode?: string
  ): Observable<Account> {
    const request = this._accountBuilder.buildAccountRegistrationRequest(
      GrantTypeEnum.facebook,
      email,
      idToken,
      locale,
      referralCode
    );

    return this.registerAccount(request);
  }

  registerAccountByApple(
    email: string,
    idToken: string,
    locale: string,
    referralCode?: string
  ): Observable<Account> {
    const request = this._accountBuilder.buildAccountRegistrationRequest(
      GrantTypeEnum.apple,
      email,
      idToken,
      locale,
      referralCode
    );

    return this.registerAccount(request);
  }

  private registerAccount(
    request: IAccountRegistrationRequest
  ): Observable<Account> {
    const url = `${environment.gatewayUrl}/register_account`;
    const requestPayload = this._accountBuilder.buildAccountRegistrationRequestPayload(
      request
    );

    return this._httpService
      .post(url, requestPayload, {}, true, 'json', true)
      .pipe(
        map(
          ({ body }: IResponseInfo) =>
            new Account(body['authentication_result'])
        ),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'SIGNUP.REGISTRATION_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  loginAccountByEmail(
    email: string,
    password: string,
    locale: string
  ): Observable<LoginResult> {
    const request = this._accountBuilder.buildAccountLoginRequest(
      GrantTypeEnum.password,
      email,
      password,
      locale
    );

    return this.loginAccount(request);
  }

  loginAccountByFacebook(
    email: string,
    idToken: string,
    locale: string
  ): Observable<LoginResult> {
    const request = this._accountBuilder.buildAccountLoginRequest(
      GrantTypeEnum.facebook,
      email,
      idToken,
      locale
    );

    return this.loginAccount(request);
  }

  loginAccountByGoogle(
    email: string,
    idToken: string,
    locale: string
  ): Observable<LoginResult> {
    const request = this._accountBuilder.buildAccountLoginRequest(
      GrantTypeEnum.google,
      email,
      idToken,
      locale
    );

    return this.loginAccount(request);
  }

  loginAccountByApple(
    email: string,
    idToken: string,
    locale: string
  ): Observable<LoginResult> {
    const request = this._accountBuilder.buildAccountLoginRequest(
      GrantTypeEnum.apple,
      email,
      idToken,
      locale
    );

    return this.loginAccount(request);
  }

  loginAccountByCompany(
    accountId: string,
    refreshToken: string,
    locale: string
  ): Observable<LoginResult> {
    const url = `${environment.gatewayUrl}/auth/identity/connect/token`;
    const requestHeaders = this._accountBuilder.buildAccountLoginRequestHeaders(
      locale,
      environment.version
    );
    const requestPayload: ICompanyLoginRequestPayload = this._accountBuilder.buildCompanyLoginRequestPayload(
      GrantTypeEnum.refresh_token,
      accountId,
      refreshToken
    );

    return this._httpService.post(url, requestPayload, requestHeaders).pipe(
      map(({ body }: IResponseInfo) => new LoginResult(body)),
      catchError((httpErrorResponse: HttpErrorResponse) => {
        try {
          const errorResponse = this._accountBuilder.buildAccountResponseError(
            httpErrorResponse
          );

          return throwError(errorResponse);
        } catch (ex) {
          const msg = this._translateService.instant('LOGIN.LOGIN_FAILED');
          return throwError(new Error(msg));
        }
      })
    );
  }

  loginAccountByChallenge(
    challenge: Challenge,
    locale: string
  ): Observable<LoginResult> {
    const url = `${environment.gatewayUrl}/auth/identity/connect/token`;
    const requestHeaders = this._accountBuilder.buildAccountLoginRequestHeaders(
      locale,
      environment.version
    );
    const requestPayload = this._accountBuilder.buildLoginByChallengePayload(
      challenge.id
    );

    return this._httpService.post(url, requestPayload, requestHeaders).pipe(
      map(({ body }: IResponseInfo) => new LoginResult(body)),
      catchError((httpErrorResponse: HttpErrorResponse) => {
        try {
          const errorResponse = this._accountBuilder.buildAccountResponseError(
            httpErrorResponse
          );

          return throwError(errorResponse);
        } catch (ex) {
          const msg = this._translateService.instant('LOGIN.LOGIN_FAILED');

          return throwError(new Error(msg));
        }
      })
    );
  }

  private loginAccount(request: IAccountLoginRequest): Observable<LoginResult> {
    const url = `${environment.gatewayUrl}/auth/identity/connect/token`;
    const requestHeaders = this._accountBuilder.buildAccountLoginRequestHeaders(
      request.locale,
      environment.version
    );
    const requestPayload: IAccountLoginRequestPayload =
      request.grantType === GrantTypeEnum.password
        ? this._accountBuilder.buildAccountLoginRequestPayload(request)
        : this._accountBuilder.buildAccountSocialLoginRequestPayload(request);

    return this._httpService.post(url, requestPayload, requestHeaders).pipe(
      map(({ body }: IResponseInfo) => new LoginResult(body)),
      catchError((httpErrorResponse: HttpErrorResponse) => {
        try {
          const errorResponse = this._accountBuilder.buildAccountResponseError(
            httpErrorResponse
          );

          return throwError(errorResponse);
        } catch (ex) {
          const msg = this._translateService.instant('LOGIN.LOGIN_FAILED');

          return throwError(new Error(msg));
        }
      })
    );
  }

  setPasswordByEmail(email: string, locale: string): Observable<IResponseInfo> {
    const requestPayload: ISetPasswordRequestPayload = this._accountBuilder.buildSetPasswordRequestPayload(
      email,
      locale
    );

    return this._httpService
      .post(`${environment.gatewayUrl}/forgot_password`, requestPayload)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'SET_PASSWORD.SET_REQUEST_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  validateSetPasswordToken(
    token: string,
    locale: string
  ): Observable<SetToken> {
    const requestPayload: ISetPasswordTokenRequestPayload = this._accountBuilder.buildValidateSetPasswordTokenRequestPayload(
      token,
      locale
    );
    return this._httpService
      .post(`${environment.gatewayUrl}/forgot_password/verify`, requestPayload)
      .pipe(
        map(({ body }: IResponseInfo) => new SetToken(body)),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'SET_PASSWORD.SET_REQUEST_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  updatePasswordByToken(
    userId: string,
    token: string,
    password: string,
    locale: string
  ): Observable<IResponseInfo> {
    const requestPayload: IUpdatePasswordRequestPayload = this._accountBuilder.buildUpdatePasswordRequestPayload(
      userId,
      token,
      password,
      locale
    );
    return this._httpService
      .post(`${environment.gatewayUrl}/forgot_password/reset`, requestPayload)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'SET_PASSWORD.SET_REQUEST_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  verifyEmailByToken(token: string, locale: string): Observable<IResponseInfo> {
    const requestPayload: IChangePasswordTokenRequestPayload = this._accountBuilder.buildChangeEmailTokenRequestPayload(
      token,
      locale
    );

    return this._httpService
      .post(`${environment.gatewayUrl}/change_email/verify`, requestPayload)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'CHANGE_EMAIL.REQUEST_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  cancelChangeEmailByToken(
    token: string,
    locale: string
  ): Observable<IResponseInfo> {
    const requestPayload: IChangePasswordTokenRequestPayload = this._accountBuilder.buildChangeEmailTokenRequestPayload(
      token,
      locale
    );

    return this._httpService
      .post(`${environment.gatewayUrl}/change_email/cancel`, requestPayload)
      .pipe(
        catchError((httpErrorResponse: HttpErrorResponse) => {
          try {
            const errorResponse = this._accountBuilder.buildAccountResponseError(
              httpErrorResponse
            );

            return throwError(errorResponse);
          } catch (ex) {
            const msg = this._translateService.instant(
              'CANCEL_CHANGE_EMAIL.REQUEST_FAILED'
            );

            return throwError(new Error(msg));
          }
        })
      );
  }

  resendUnusualLoginEmail(attemptId: string): Observable<IResponseInfo> {
    const url = `${environment.gatewayUrl}/auth/login_attempt/${attemptId}/resend_email`;

    return this._httpService.post(url);
  }

  verifyUnusualLogin(
    attemptId: string,
    verificationCode: string
  ): Observable<LoginAttemptResult> {
    const url = `${environment.gatewayUrl}/auth/login_attempt/${attemptId}/allow`;
    const params = {
      verification_code: verificationCode,
    };

    return this._httpService
      .post(url, params)
      .pipe(map(({ body }: IResponseInfo) => new LoginAttemptResult(body)));
  }

  verifyEmail(attemptId, verificationCode) {
    return this._httpService.post(
      `${environment.gatewayUrl}/verification/public/email_verify_attempt/${attemptId}/allow`,
      {
        verification_code: verificationCode,
      }
    );
  }
}
